import React from 'react'
import { useMutation } from '@apollo/client'
import { Merchant } from '@slerp/controls'
import { StripeConnect } from '../Utils/Stripe'
import { useMerchant } from 'hooks/useGlobalContext/useMerchant'
import {
  DISCONNECT_STRIPE_SETTINGS,
  RECONNECT_STRIPE_SETTINGS
} from './actions'
import { useStripeDisconnect, useStripeReconnect } from 'components/Common'

const StripeConnectSettings = (props: { merchant: Merchant }) => {
  const {
    stripeConnectData,
    refetchStripeConnectData,
    setStripeConnectData,
    refetchMerchantData
  } = useMerchant()
  const { merchant } = props

  const [disconnectStripeSettings] = useMutation(DISCONNECT_STRIPE_SETTINGS, {
    fetchPolicy: 'no-cache'
  })
  const [reconnectStripeSettings] = useMutation(RECONNECT_STRIPE_SETTINGS, {
    fetchPolicy: 'no-cache'
  })

  const { modals, openConfirm, disconnecting } = useStripeDisconnect({
    disconnect: () =>
      disconnectStripeSettings({ variables: { merchantId: merchant?.id } }),
    onSuccess: refetchStripeConnectData,
    invoicesPath: '/settings/accounts-and-billing/invoices'
  })

  // StripeConnectLoader only re-queries when stripeConnectData is falsy
  // (fetch-once gate, doc/ARCHITECTURE.md §6.2) - clear it so the loader
  // refetches instead of calling its own skipped refetch.
  const refreshStripeConnectData = () => {
    setStripeConnectData(null)
    refetchMerchantData()
  }

  const {
    modals: reconnectModals,
    openReconnect,
    reconnecting,
    canReconnect
  } = useStripeReconnect({
    reconnect: (stripeAccountId) =>
      reconnectStripeSettings({
        variables: { merchantId: merchant?.id, stripeAccountId }
      }),
    onSuccess: refreshStripeConnectData,
    invoicesPath: '/settings/accounts-and-billing/invoices',
    previousAccounts: stripeConnectData?.previousAccounts ?? [],
    hasConnected: !!stripeConnectData?.hasConnected
  })

  return (
    <>
      <StripeConnect
        stripeId={merchant?.stripe_settings?.[0]?.stripe_user_id}
        hasStripeCustomerId={!!merchant?.subscription?.stripe_customer_id}
        stripeConnectUrl={stripeConnectData}
        onDisconnect={openConfirm}
        disconnecting={disconnecting}
        onReconnect={openReconnect}
        reconnecting={reconnecting}
        canReconnect={canReconnect}
      />
      {modals}
      {reconnectModals}
    </>
  )
}

export default StripeConnectSettings
